import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { HardwareRequestItem, HardwareRequestItemFromOptions, QuoteHardwareRequestItemStore } from "../../../powershadesApiTypes";
import { ThunkAPI } from "../types";
import apiCalls from "../../../PowerShadesAPIFunctions";
import { extraEntityReducersFunction, loadStatus } from "../entitiesType";
import { captureSentryError } from "../../../psUtil";
import type { AppState } from "../../index";
import { wentToQuote } from "../quotes";

const HW_ITEM_DIAGNOSTIC_TOLERANCE = 0.01;
const sentHardwareAnomalyEvents = new Set<string>();

const nearlyEqual = (left: number, right: number, tolerance = HW_ITEM_DIAGNOSTIC_TOLERANCE) => (
	Math.abs((left ?? 0) - (right ?? 0)) <= tolerance
);

const reportPotentialLineTotalStoredAsUnit = ({
	quoteId,
	sourceAction,
	hardwareItem,
	hardwareCatalogPart
}: {
	quoteId: number;
	sourceAction: string;
	hardwareItem: HardwareRequestItem;
	hardwareCatalogPart: HardwareRequestItemFromOptions | undefined;
}) => {
	const quantity = hardwareItem.quantity ?? 0;
	const observedMsrp = hardwareItem.msrp ?? 0;
	const catalogUnitMsrp = hardwareCatalogPart?.msrp ?? 0;

	if (!hardwareCatalogPart || quantity <= 1 || observedMsrp <= 0 || catalogUnitMsrp <= 0) {
		return;
	}

	const expectedLineTotal = catalogUnitMsrp * quantity;
	const looksLikeLineTotalInUnitField = nearlyEqual(observedMsrp, expectedLineTotal)
        && !nearlyEqual(observedMsrp, catalogUnitMsrp);

	if (!looksLikeLineTotalInUnitField) {
		return;
	}

	const expectedUnitShippingFromFallback = catalogUnitMsrp * 0.035;
	const expectedLineShippingFromFallback = expectedUnitShippingFromFallback * quantity;
	const observedShipping = hardwareItem.shipping ?? 0;
	const shippingAlsoLooksLikeLineTotal = observedShipping > 0
        && nearlyEqual(observedShipping, expectedLineShippingFromFallback)
        && !nearlyEqual(observedShipping, expectedUnitShippingFromFallback);

	const dedupeKey = [
		sourceAction,
		quoteId,
		hardwareItem.line_number,
		hardwareItem.sku,
		quantity,
		observedMsrp,
		catalogUnitMsrp
	].join("|");

	if (sentHardwareAnomalyEvents.has(dedupeKey)) {
		return;
	}

	sentHardwareAnomalyEvents.add(dedupeKey);

	captureSentryError(new Error("Hardware MSRP likely stored as line total instead of per-unit"), {
		tags: {
			section: "redux",
			pricing_area: "hardware_request_items",
			anomaly_type: "hardware_msrp_line_total_stored_as_unit"
		},
		extra: {
			redux_action: sourceAction,
			file_origin: "src/Store/entities/hardwareRequestItems/index.ts",
			quote_id: quoteId,
			line_number: hardwareItem.line_number,
			sku: hardwareItem.sku,
			quantity,
			observed_msrp_value: observedMsrp,
			expected_unit_msrp_from_catalog: catalogUnitMsrp,
			expected_line_total_from_catalog: expectedLineTotal,
			observed_shipping_value: observedShipping,
			expected_unit_shipping_from_fallback: expectedUnitShippingFromFallback,
			expected_line_shipping_from_fallback: expectedLineShippingFromFallback,
			shipping_likely_also_line_total_as_unit: shippingAlsoLooksLikeLineTotal
		}
	});
};

const reportHardwarePriceAnomalies = ({
	quoteId,
	sourceAction,
	hardwareItems,
	hardwareCatalogMap
}: {
	quoteId: number;
	sourceAction: string;
	hardwareItems: HardwareRequestItem[];
	hardwareCatalogMap: Record<string, HardwareRequestItemFromOptions>;
}) => {
	hardwareItems.forEach((hardwareItem) => {
		const sku = hardwareItem?.sku;

		if (!sku) {
			return;
		}

		const catalogPart = hardwareCatalogMap[sku];

		reportPotentialLineTotalStoredAsUnit({
			quoteId,
			sourceAction,
			hardwareItem,
			hardwareCatalogPart: catalogPart
		});
	});
};

const loadHwItemsByQuoteId = createAsyncThunk<HardwareRequestItem[], number, ThunkAPI>(
	'entities/GetHwItems',
	async (quoteId, { rejectWithValue, getState }) => {
		let hwItems: HardwareRequestItem[] = [];

		try {
			const resp = await apiCalls.getHwParts(quoteId);

			hwItems = resp.data.hardware_request_items;

			const appState = getState() as AppState;
			const hardwareCatalogMap = (appState.entity.hardwareRequestItems[quoteId]?.TotalItems ?? {}) as unknown as Record<string, HardwareRequestItemFromOptions>;
			reportHardwarePriceAnomalies({
				quoteId,
				sourceAction: 'entities/GetHwItems',
				hardwareItems: hwItems,
				hardwareCatalogMap
			});
		} catch (err: any) {
			const error: AxiosError = err;

			console.error(error);

			return rejectWithValue(false);
		}

		return hwItems;
	}
);

const addQuoteHwItem = createAsyncThunk<
	HardwareRequestItem, { quoteId: number, sku: string, lineItemNumber: number }, ThunkAPI
>(
	'entities/AddHwItem',
	async ({ quoteId, sku, lineItemNumber }, { rejectWithValue, dispatch, getState }) => {
		let hwItem;

		try {
			await dispatch(wentToQuote(quoteId));

			const resp = await apiCalls.createHWItem(quoteId, sku, lineItemNumber);

			hwItem = resp.data.hardware_request_items.find((item: HardwareRequestItem) => item.line_number === lineItemNumber);

			const appState = getState() as AppState;
			const hardwareCatalogMap = (appState.entity.hardwareRequestItems[quoteId]?.TotalItems ?? {}) as unknown as Record<string, HardwareRequestItemFromOptions>;
			reportHardwarePriceAnomalies({
				quoteId,
				sourceAction: 'entities/AddHwItem',
				hardwareItems: hwItem ? [hwItem] : [],
				hardwareCatalogMap
			});
		} catch (err: any) {
			const error: AxiosError = err;

			console.error(error);

			return rejectWithValue(false);
		}

		return hwItem;
	}
);

const deleteQuoteHwItem = createAsyncThunk<
	{ quoteId: number, lineNumber: number }, { quoteId: number, lineNumber: number }, ThunkAPI
>(
	'entities/DeleteHwItem',
	async ({ quoteId, lineNumber }, { rejectWithValue, dispatch }) => {
		try {
			await dispatch(wentToQuote(quoteId));
			await apiCalls.deleteHWItem(quoteId, lineNumber);
		} catch (err: any) {
			const error: AxiosError = err;

			console.error(error);

			return rejectWithValue(false);
		}

		return { quoteId, lineNumber };
	}
);

const updateQuoteHwItem = createAsyncThunk<
    HardwareRequestItem | null, { quoteId: number, lineNumber: number, quantity: number }, ThunkAPI
>(
	'entities/UpdateHwItem',
	async ({ quoteId, lineNumber, quantity }, { rejectWithValue, dispatch, getState }) => {
		let hwItem: HardwareRequestItem | null;

		await dispatch(wentToQuote(quoteId));

		try {
			const resp = await apiCalls.updateHWItem(quoteId, lineNumber, quantity);

			hwItem = resp?.data?.hardware_request_items?.find((item: HardwareRequestItem) => item.line_number === lineNumber) ?? null;

			const appState = getState() as AppState;
			const hardwareCatalogMap = (appState.entity.hardwareRequestItems[quoteId]?.TotalItems ?? {}) as unknown as Record<string, HardwareRequestItemFromOptions>;
			reportHardwarePriceAnomalies({
				quoteId,
				sourceAction: 'entities/UpdateHwItem',
				hardwareItems: hwItem ? [hwItem] : [],
				hardwareCatalogMap
			});
		} catch (err: any) {
			const error: AxiosError = err;

			console.error(error);

			return rejectWithValue(false);
		}

		return hwItem;
	}
);

const getFullListOfParts = createAsyncThunk<
	HardwareRequestItemFromOptions[], { quoteId: number }, ThunkAPI
>(
	'entities/GetFullListOfParts',
	async ({ quoteId }, { rejectWithValue }) => {
		let hwItems: HardwareRequestItemFromOptions[] = [];

		try {
			const resp = await apiCalls.getAllowedHwParts(quoteId);

			if (resp.data && Array.isArray(resp.data.parts)) {
				hwItems = resp.data.parts;
			} else {
				console.warn('getFullListOfParts: Unexpected response structure', resp.data);
				return [];
			}
		} catch (err: any) {
			const error: AxiosError = err;

			console.error('getFullListOfParts error:', error);

			return rejectWithValue(false);
		}

		return hwItems;
	}
);

const quoteHWItemsBuilder: extraEntityReducersFunction = (builder) => {
	builder.addCase(getFullListOfParts.pending, (state, action) => {
		const currentState = state.hardwareRequestItems[action.meta.arg.quoteId];
		if (currentState) currentState.fullLoadStatus = loadStatus.loading;
		state.hardwareRequestItems[action.meta.arg.quoteId] = currentState ?? {
			Items: {},
			loadStatus: loadStatus.loading,
			TotalItems: {},
			fullLoadStatus: loadStatus.needsLoaded
		};
	});

	builder.addCase(getFullListOfParts.fulfilled, (state, action) => {
		let record = state.hardwareRequestItems[action.meta.arg.quoteId];
        
		if (!action.payload || !Array.isArray(action.payload)) {
			console.warn('getFullListOfParts.fulfilled: Invalid payload received', action.payload);
			if (!record) {
				record = {
					Items: {},
					loadStatus: loadStatus.fullyLoaded,
					TotalItems: {},
					fullLoadStatus: loadStatus.needsLoaded
				};
				state.hardwareRequestItems[action.meta.arg.quoteId] = record;
			}
			record.fullLoadStatus = loadStatus.needsLoaded;
			return;
		}
     
		const itemsStore: HardwareRequestItemFromOptions[] = action.payload.map((hwi) => ({
			name: hwi.name ?? "",
			partNumber: hwi.partNumber ?? "",
			msrp: hwi.msrp ?? 0,
			uom: hwi.uom ?? "",
			cost: hwi.cost ?? 0,
			quantity: hwi.quantity ?? 0,
		}));
            
		const itemsRecord = itemsStore.reduce((acc, item) => {
			acc[item.partNumber] = item;
			return acc;
		}, {} as Record<string, HardwareRequestItemFromOptions>);

		if (!record) {
			record = {
				Items: {},
				loadStatus: loadStatus.fullyLoaded,
				TotalItems: {},
				fullLoadStatus: loadStatus.needsLoaded
			};
			state.hardwareRequestItems[action.meta.arg.quoteId] = record;
		}

		record.fullLoadStatus = loadStatus.fullyLoaded;
		record.TotalItems = { ...itemsRecord };
	});

	builder.addCase(getFullListOfParts.rejected, (state, action) => {
		let record = state.hardwareRequestItems[action.meta.arg.quoteId];
        
		if (!record) {
			record = {
				Items: {},
				loadStatus: loadStatus.fullyLoaded,
				TotalItems: {},
				fullLoadStatus: loadStatus.needsLoaded
			};
			state.hardwareRequestItems[action.meta.arg.quoteId] = record;
		}
        
		record.fullLoadStatus = loadStatus.needsLoaded;
		console.warn('getFullListOfParts failed for quoteId:', action.meta.arg.quoteId, action.error);
	});

	builder.addCase(loadHwItemsByQuoteId.pending, (state, action) => {
		const currentState = state.hardwareRequestItems[action.meta.arg];
		if (currentState) currentState.loadStatus = loadStatus.loading;
		state.hardwareRequestItems[action.meta.arg] = currentState ?? {
			Items: {},
			loadStatus: loadStatus.loading,
			TotalItems: {},
			fullLoadStatus: loadStatus.needsLoaded
		};
	});

	builder.addCase(loadHwItemsByQuoteId.fulfilled, (state, action) => {
		let record = state.hardwareRequestItems[action.meta.arg];

		const itemsStore: QuoteHardwareRequestItemStore[] = action.payload.map((hwi) => ({
			loadStatus: loadStatus.fullyLoaded,
			Id: hwi.line_number ?? 0,
			name: hwi.sku ?? "",
			mfg_status: "",
			quantity: hwi.quantity ?? 0,
			last_time_priced: 0,
			part_number: hwi.sku ?? "",
			is_pricing: false,
			quote_id: hwi.quote_id ?? 0,
			msrp: hwi.msrp ?? 0,
			cost: hwi.cost ?? 0,
			line_number: hwi.line_number ?? 0,
			shipping: hwi.shipping ?? ((hwi?.msrp ?? 0) * 0.35),
			sku: hwi.sku ?? "",
			is_fascia_combination: hwi.is_fascia_combination ?? false
		}));

		const itemsRecord = itemsStore.reduce((acc, item) => {
			acc[item.line_number] = item;
			return acc;
		}, {} as Record<number, QuoteHardwareRequestItemStore>);
		if (!record) {
			record = {
				Items: {},
				loadStatus: loadStatus.fullyLoaded,
				TotalItems: {},
				fullLoadStatus: loadStatus.needsLoaded
			};
		}

		record.Items = { ...itemsRecord };
	});

	builder.addCase(loadHwItemsByQuoteId.rejected, (state, action) => {
		state.hardwareRequestItems[action.meta.arg] = {
			Items: {},
			loadStatus: loadStatus.needsLoaded,
			TotalItems: {},
			fullLoadStatus: loadStatus.needsLoaded
		};
	});

	builder.addCase(addQuoteHwItem.pending, (state, action) => {
		const { quoteId } = action.meta.arg;
		const line_number = action.meta.arg.lineItemNumber;
		const { sku } = action.meta.arg;

		let record = state.hardwareRequestItems[quoteId];

		if (!record) {
			record = {
				Items: {},
				loadStatus: loadStatus.fullyLoaded,
				TotalItems: {},
				fullLoadStatus: loadStatus.needsLoaded
			};
			state.hardwareRequestItems[quoteId] = record;
		}

		record.Items[line_number] = {
			Id: line_number,
			mfg_status: "",
			last_time_priced: 0,
			is_pricing: false,
			quote_id: quoteId,
			part_number: sku,
			shipping: 0,
			quantity: 1,
			name: "",
			msrp: 0,
			cost: 0,
			line_number,
			is_fascia_combination: false
		};
	});
	builder.addCase(addQuoteHwItem.fulfilled, (state, action) => {
		const { quoteId } = action.meta.arg;
		const line_number = action.meta.arg.lineItemNumber;
		const { sku } = action.meta.arg;
		let record = state.hardwareRequestItems[quoteId];

		if (!record) {
			record = {
				Items: {},
				loadStatus: loadStatus.needsLoaded,
				TotalItems: {},
				fullLoadStatus: loadStatus.fullyLoaded
			};
			state.hardwareRequestItems[quoteId] = record;
		}

		record.Items[line_number] = {
			Id: line_number,
			mfg_status: "",
			last_time_priced: 0,
			is_pricing: false,
			quote_id: quoteId,
			part_number: action.payload?.sku ?? sku,
			shipping: action.payload.shipping ?? ((action?.payload?.msrp ?? 0) * 0.035),
			quantity: action.payload.quantity ?? 0,
			name: action.payload?.sku,
			msrp: action.payload.msrp ?? 0,
			cost: 0,
			line_number,
			is_fascia_combination: action.payload.is_fascia_combination ?? false
		};
	});

	builder.addCase(deleteQuoteHwItem.pending, (state, action) => {
		const record = state.hardwareRequestItems[action.meta.arg.quoteId];

		if (record) {
			delete record.Items[action.meta.arg.lineNumber];
		}
	});

	builder.addCase(deleteQuoteHwItem.fulfilled, (state, action) => {
		const record = state.hardwareRequestItems[action.payload.quoteId];

		if (record) {
			delete record.Items[action.payload.lineNumber];
		}
	});

	builder.addCase(updateQuoteHwItem.fulfilled, (state, action) => {
		const {
			quote_id = action.meta.arg.quoteId,
			line_number,
			quantity
		} = action.payload ?? {
			quote_id: action.meta.arg.quoteId,
			line_number: action.meta.arg.lineNumber,
			quantity: 0
		};
		const record = state.hardwareRequestItems[quote_id];

		if (record) {
			const hwItemRecord = record.Items[line_number];
			if (hwItemRecord) hwItemRecord.quantity = quantity ?? 0;

			if ((hwItemRecord?.quantity ?? 0) === 0) {
				delete record.Items[line_number];
			}
		}
	});

	builder.addCase(updateQuoteHwItem.rejected, (state, action) => {
		const { quoteId, lineNumber, quantity } = action.meta.arg;
		const record = state.hardwareRequestItems[quoteId];

		if (record) {
			record.loadStatus = loadStatus.fullyLoaded;
		}

		captureSentryError(new Error('Failed to update hardware item quantity'), {
			tags: {
				section: 'redux',
				pricing_area: 'hardware_request_items',
				redux_action: 'entities/UpdateHwItem.rejected'
			},
			extra: {
				file_origin: 'src/Store/entities/hardwareRequestItems/index.ts',
				quote_id: quoteId,
				line_number: lineNumber,
				attempted_quantity: quantity,
				error_message: action.error?.message,
				rejected_payload: action.payload
			}
		});
	});
};

export {
	quoteHWItemsBuilder,
	loadHwItemsByQuoteId,
	addQuoteHwItem,
	deleteQuoteHwItem,
	updateQuoteHwItem,
	getFullListOfParts
};
